import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import TextField from 'Components/UI/TextField';
import Typography from '@material-ui/core/Typography';
import { CircularProgress } from '@material-ui/core';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import EventIcon from '@material-ui/icons/Event';
import Button from 'Components/UI/Button';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import { createInputBaseStyles } from 'Components/UI/styles';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { fullDateAtTime, shortDateAtTime } from 'Consts/DateFormats';

const styles = (theme) => ({
  root: {},
  subjectLine: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  header: {},
  subjectInputBase: {
    ...createInputBaseStyles(theme),
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightBold,
    paddingBottom: theme.spacing(0),
    '& input': {
      padding: theme.spacing(1),
    },
    '& label': {
      transform: 'translate(14px, 10px) scale(1)',
    },
  },
  actionBar: {
    display: 'flex',
    flex: 1,
    margin: theme.spacing(2, 0),
    alignItems: 'center',
  },
  dateTime: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  dateText: {
    color: theme.palette.facet.accentBlue2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dateTextIcon: {
    marginRight: theme.spacing(0.5),
  },
  buttons: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
  },
  button: {
    margin: theme.spacing(0.5),
    '&:last-child': {
      marginRight: theme.spacing(0),
    },
  },
  editButton: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
  },
  content: {
    border: '1px solid',
    borderColor: theme.palette.facet.mediumGray,
    borderRadius: 4,
    padding: theme.spacing(1),
    minHeight: '300px',
  },
});

const ScheduleSendReviewForm = ({
  classes,
  meetingResource,
  meetingAgendaResource,
  onClickEdit,
  closeModal,
  refreshResources,
}) => {
  const [reviewLoading, setReviewLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { logEvent } = useEventTrackingLogEvent();

  const toggleReviewed = async () => {
    setReviewLoading(true);
    const originalReviewedState = meetingAgendaResource.attr('isReviewed');
    logEvent(`Scheduled Send: Marking scheduled agenda as ${originalReviewedState === 'true' ? 'not reviewed' : 'reviewed'}`, {
      householdId: meetingAgendaResource.attr('householdId'),
      meetingDate: meetingResource ? moment(meetingResource.attr('startTime')).format(fullDateAtTime) : null,
      scheduledDate: moment(meetingAgendaResource.attr('scheduledSendCustomDate')).format(fullDateAtTime),
      daysUntilMeeting: meetingResource ? moment(meetingResource.attr('startTime')).diff(moment(), 'days') : null,
      messageType: meetingAgendaResource.attr('messageType'),
    });
    meetingAgendaResource.attr('isReviewed', originalReviewedState === 'true' ? 'false' : 'true');
    try {
      await meetingAgendaResource.submit();
      refreshResources();
      closeModal();
    } catch (err) {
      meetingAgendaResource.attr('isReviewed', originalReviewedState);
      displayErrorMessage(err, err.errors);
    }

    setReviewLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    await meetingAgendaResource.delete();
    refreshResources();
    setDeleteLoading(false);
    closeModal();
  };

  const scheduleSendDate = moment(meetingAgendaResource.attr('sendAt'));
  const meetingDateTime = moment(meetingResource.attr('startTime'));
  const reviewedToggleText = meetingAgendaResource.attr('isReviewed') === 'true' ? 'Mark as Not Reviewed' : 'Mark as Reviewed';
  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.subjectLine}>
          <TextField
            classes={{
              root: classes.subjectInputBase,
            }}
            label="Subject"
            fullWidth
            variant="outlined"
            disabled
            value={meetingAgendaResource.attr('subject')}
          />
        </div>
        <div className={classes.actionBar}>
          <div className={classes.dateTime}>
            <Typography variant="subtitle2" className={classes.dateText}>
              <QueryBuilderIcon
                fontSize="small"
                className={classes.dateTextIcon}
              />
              {`Scheduled to send on: ${scheduleSendDate.format(shortDateAtTime)}`}
            </Typography>
            <Typography variant="subtitle2" className={classes.dateText}>
              <EventIcon
                fontSize="small"
                className={classes.dateTextIcon}
              />
              {`For meeting on: ${meetingDateTime.format(shortDateAtTime)}`}
            </Typography>
          </div>
          <div className={classes.buttons}>
            <Button
              className={classes.button}
              variant="contained"
              color={meetingAgendaResource.attr('isReviewed') === 'true' ? 'default' : 'secondary'}
              onClick={toggleReviewed}
              disabled={reviewLoading || deleteLoading}
            >
              {reviewLoading ? <CircularProgress size={24} /> : reviewedToggleText}
            </Button>
            <Button className={classes.button} variant="contained" color="red" onClick={handleDelete} disabled={reviewLoading || deleteLoading}>
              {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
      <div className={classes.content}>
        <RichTextDisplay content={meetingAgendaResource.attr('content')} />
        <div className={classes.editButton}>
          <Button className={classes.button} variant="contained" color="accentBlue" onClick={onClickEdit}>Edit</Button>
        </div>
      </div>
    </div>
  );
};

ScheduleSendReviewForm.propTypes = {
  classes: PropTypes.object.isRequired,
  meetingResource: PropTypes.object.isRequired,
  meetingAgendaResource: PropTypes.object.isRequired,
  onClickEdit: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  refreshResources: PropTypes.func.isRequired,
};

export default withStyles(styles)(ScheduleSendReviewForm);
